import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "delivery-issues-wrapper" }
const _hoisted_2 = { class: "issues-header" }
const _hoisted_3 = { class: "issues-grid-container" }
const _hoisted_4 = {
  key: 0,
  class: "loading-state"
}
const _hoisted_5 = {
  key: 1,
  class: "error-state"
}
const _hoisted_6 = {
  key: 2,
  class: "empty-state"
}
const _hoisted_7 = {
  key: 3,
  class: "grid-content"
}

import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useNotifications } from '@/composables/useNotifications';
import { usePermissions } from '@/composables/usePermissions';
import { useAuthStore } from '@/stores';

// Components
import AppButton from '../../base/AppButton.vue';
import AppDataGrid from '../../base/AppDataGrid.vue';
import IssueFormModal from './IssueFormModal.vue';
import ConfirmModal from '../../common/ConfirmModal.vue';
import PermissionGuard from '../../common/PermissionGuard.vue';

// Services
import deliveryIssuesService from '@/services/deliveryIssues.service';
import intervenantsService from '@/services/intervenants.service';
import { PermissionType } from '@/services/permission.service';
import type { Issue } from '@/types/issue';

interface Intervenant {
  id: number;
  name: string;
}

interface Props {
  projectId: string | number;
}

interface Emits {
  'issue-created': [issue: Issue];
  'issue-updated': [issue: Issue];
  'issue-deleted': [issue: Issue];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DeliveryIssues',
  props: {
    projectId: {}
  },
  emits: ["issue-created", "issue-updated", "issue-deleted"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;
const emit = __emit;

const { t } = useI18n();
const { notifySuccess, notifyError } = useNotifications();
const { hasPermissionForProject } = usePermissions();
const router = useRouter();

// Reactive data
const issues = ref<Issue[]>([]);
const intervenants = ref<Intervenant[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const gridApi = ref<any>(null);

// Modal states
const showIssueModal = ref(false);
const selectedIssue = ref<Issue | null>(null);
const showDeleteConfirm = ref(false);
const issueToDelete = ref<Issue | null>(null);

// Computed properties
const deleteMessage = computed(() => {
  if (!issueToDelete.value) return '';
  return t('issues.confirmation.deleteIssue');
});

// Grid configuration
const defaultColDef = ref({
  resizable: true,
  sortable: true,
  filter: false,
  floatingFilter: false
});

// Column definitions - make reactive to language changes
const columnDefs = computed(() => [
  {
    field: 'title',
    headerName: t('issues.title'),
    type: 'text',
    flex: 2,
    minWidth: 200,
    sortable: true,
    resizable: true
  },
  {
    field: 'status',
    headerName: t('issues.status'),
    width: 140,
    sortable: true,
    resizable: true,
    cellRenderer: (params: any) => {
      const status = params.value;
      const statusClass = `status-badge status-${status?.toLowerCase()}`;
      return `<span class="${statusClass}">${t(`issues.statuses.${status}`)}</span>`;
    }
  },
  {
    field: 'priority',
    headerName: t('issues.priority'),
    width: 120,
    sortable: true,
    resizable: true,
    cellRenderer: (params: any) => {
      const priority = params.value;
      const priorityClass = `priority-badge priority-${priority?.toLowerCase()}`;
      return `<span class="${priorityClass}">${t(`issues.priorities.${priority}`)}</span>`;
    }
  },
  {
    field: 'reporterName',
    headerName: t('issues.reportedBy'),
    type: 'text',
    width: 150,
    sortable: true,
    resizable: true
  },
  {
    field: 'assigneeName',
    headerName: t('issues.assignedTo'),
    type: 'text',
    width: 150,
    sortable: true,
    resizable: true
  },
  {
    field: 'createdAt',
    headerName: t('issues.reportedDate'),
    type: 'date',
    width: 140,
    sortable: true,
    resizable: true
  },
  {
    field: 'resolvedDate',
    headerName: t('issues.resolvedDate'),
    type: 'date',
    width: 140,
    sortable: true,
    resizable: true
  }
]);

// Get current user ID
const authStore = useAuthStore();

// Define actions - make reactive to language changes
const actions = computed(() => {
  const currentUserId = authStore.getUser?.id;
  const availableActions = [];
  
  // Always add view details action
  availableActions.push({
    name: 'viewDetails',
    label: t('issues.details.viewDetails'),
    icon: 'fas fa-eye',
    variant: 'primary'
  });
  
  // Edit action - only show if current user is the creator
  if (hasPermissionForProject(PermissionType.UPDATE_ISSUE, props.projectId).value) {
    availableActions.push({
      name: 'edit',
      label: t('common.edit'),
      icon: 'fas fa-edit',
      variant: 'secondary',
      condition: (rowData: Issue) => rowData.createdBy === currentUserId
    });
  }
  
  // Delete action - only show if current user is the creator
  if (hasPermissionForProject(PermissionType.DELETE_ISSUE, props.projectId).value) {
    availableActions.push({
      name: 'delete',
      label: t('common.delete'),
      icon: 'fas fa-trash',
      variant: 'danger',
      condition: (rowData: Issue) => rowData.createdBy === currentUserId
    });
  }
  
  return availableActions;
});

// Methods
const fetchIssues = async () => {
  try {
    loading.value = true;
    error.value = null;

    const response = await deliveryIssuesService.getIssues(props.projectId.toString());
    issues.value = response || [];
    
  } catch (err: any) {
    console.error('Error fetching issues:', err);
    error.value = err.message || t('issues.errors.failedToLoad');
  } finally {
    loading.value = false;
  }
};

const fetchIntervenants = async () => {
  try {
    const response = await intervenantsService.getIntervenants(props.projectId.toString());
    if (response && Array.isArray(response)) {
      intervenants.value = response.map((i: any) => ({
        id: i.id,
        name: i.name
      }));
    }
  } catch (err: any) {
    console.error('Error fetching intervenants:', err);
  }
};

const openCreateIssueModal = () => {
  selectedIssue.value = null;
  showIssueModal.value = true;
};

const closeIssueModal = () => {
  showIssueModal.value = false;
  selectedIssue.value = null;
};

const viewIssue = (issue: Issue) => {
  selectedIssue.value = { ...issue };
  showIssueModal.value = true;
};

const deleteIssue = (issue: Issue) => {
  issueToDelete.value = issue;
  showDeleteConfirm.value = true;
};

// Grid event handlers
const handleAction = (action: any, data: any) => {
  switch (action.name) {
    case 'viewDetails':
      router.push(`/project/${props.projectId}/issues/${data.id}`);
      break;
    case 'edit':
      viewIssue(data);
      break;
    case 'delete':
      deleteIssue(data);
      break;
    default:
      console.warn('Unknown action:', action.name);
  }
};

const onGridReady = (api: any) => {
  console.log('AppDataGrid is ready', api);
  gridApi.value = api;
};

const onIssueSaved = async (issue: Issue, isNew: boolean) => {
  const message = isNew ? 
    t('issues.messages.created') : 
    t('issues.messages.updated');
  notifySuccess(message);
  
  emit(isNew ? 'issue-created' : 'issue-updated', issue);
  await fetchIssues();
};

const confirmDelete = async () => {
  if (!issueToDelete.value) return;
  
  try {
    await deliveryIssuesService.deleteIssue(
      props.projectId.toString(),
      issueToDelete.value.id.toString()
    );
    
    notifySuccess(t('issues.messages.deleted'));
    
    emit('issue-deleted', issueToDelete.value);
    await fetchIssues();
    
  } catch (err: any) {
    console.error('Error deleting issue:', err);
    notifyError(err.message || t('issues.errors.failedToDelete'));
  } finally {
    showDeleteConfirm.value = false;
    issueToDelete.value = null;
  }
};

const cancelDelete = () => {
  showDeleteConfirm.value = false;
  issueToDelete.value = null;
};

// Lifecycle
onMounted(() => {
  fetchIssues();
  fetchIntervenants();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[1] || (_cache[1] = _createElementVNode("h3", { class: "issues-title" }, null, -1)),
      _createVNode(PermissionGuard, { permission: "CREATE_ISSUE" }, {
        default: _withCtx(() => [
          _createVNode(AppButton, {
            onClick: openCreateIssueModal,
            variant: "primary",
            class: "create-issue-btn"
          }, {
            default: _withCtx(() => [
              _cache[0] || (_cache[0] = _createElementVNode("svg", {
                viewBox: "0 0 24 24",
                width: "20",
                height: "20",
                class: "btn-icon"
              }, [
                _createElementVNode("path", {
                  fill: "currentColor",
                  d: "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
                })
              ], -1)),
              _createTextVNode(" " + _toDisplayString(_ctx.$t('issues.addIssue')), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _createElementVNode("div", _hoisted_3, [
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _cache[2] || (_cache[2] = _createElementVNode("div", { class: "loading-spinner" }, null, -1)),
            _createElementVNode("h4", null, _toDisplayString(_ctx.$t('common.loading')), 1),
            _createElementVNode("p", null, _toDisplayString(_ctx.$t('issues.fetchingIssues')), 1)
          ]))
        : (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _cache[4] || (_cache[4] = _createElementVNode("div", { class: "error-icon" }, [
                _createElementVNode("svg", {
                  viewBox: "0 0 24 24",
                  width: "48",
                  height: "48"
                }, [
                  _createElementVNode("path", {
                    fill: "currentColor",
                    d: "M12,2L13.09,8.26L22,9L13.09,9.74L12,16L10.91,9.74L2,9L10.91,8.26L12,2M12,21.5A9.5,9.5 0 0,1 2.5,12A9.5,9.5 0 0,1 12,2.5A9.5,9.5 0 0,1 21.5,12A9.5,9.5 0 0,1 12,21.5M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20Z"
                  })
                ])
              ], -1)),
              _createElementVNode("h4", null, _toDisplayString(_ctx.$t('common.error')), 1),
              _createElementVNode("p", null, _toDisplayString(error.value), 1),
              _createVNode(AppButton, {
                onClick: fetchIssues,
                variant: "outline",
                class: "retry-btn"
              }, {
                default: _withCtx(() => [
                  _cache[3] || (_cache[3] = _createElementVNode("svg", {
                    viewBox: "0 0 24 24",
                    width: "16",
                    height: "16"
                  }, [
                    _createElementVNode("path", {
                      fill: "currentColor",
                      d: "M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                    })
                  ], -1)),
                  _createTextVNode(" " + _toDisplayString(_ctx.$t('common.retry')), 1)
                ]),
                _: 1
              })
            ]))
          : (issues.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _cache[6] || (_cache[6] = _createElementVNode("div", { class: "empty-icon" }, [
                  _createElementVNode("svg", {
                    viewBox: "0 0 24 24",
                    width: "64",
                    height: "64"
                  }, [
                    _createElementVNode("path", {
                      fill: "currentColor",
                      d: "M13,9H18.5L13,3.5V9M6,2H14L20,8V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V4C4,2.89 4.89,2 6,2M15,18V16H6V18H15M18,14V12H6V14H18Z"
                    })
                  ])
                ], -1)),
                _createElementVNode("h3", null, _toDisplayString(_ctx.$t('issues.noIssuesFound')), 1),
                _createElementVNode("p", null, _toDisplayString(_ctx.$t('issues.getStarted')), 1),
                _createVNode(PermissionGuard, { permission: "CREATE_ISSUE" }, {
                  default: _withCtx(() => [
                    _createVNode(AppButton, {
                      onClick: openCreateIssueModal,
                      variant: "primary",
                      class: "create-first-issue"
                    }, {
                      default: _withCtx(() => [
                        _cache[5] || (_cache[5] = _createElementVNode("svg", {
                          viewBox: "0 0 24 24",
                          width: "20",
                          height: "20"
                        }, [
                          _createElementVNode("path", {
                            fill: "currentColor",
                            d: "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
                          })
                        ], -1)),
                        _createTextVNode(" " + _toDisplayString(_ctx.$t('issues.addIssue')), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createVNode(AppDataGrid, {
                  columnDefs: columnDefs.value,
                  rowData: issues.value,
                  defaultColDef: defaultColDef.value,
                  pagination: true,
                  pageSize: 15,
                  pageSizeOptions: [15, 50, 100],
                  suppressPaginationPanel: false,
                  animateRows: true,
                  showSearch: true,
                  searchPlaceholder: _ctx.$t('issues.placeholders.searchIssues'),
                  onGridReady: onGridReady,
                  onActionClicked: handleAction,
                  suppressMovableColumns: false,
                  gridStyle: "width: 100%;",
                  domLayout: "autoHeight",
                  actions: actions.value
                }, null, 8, ["columnDefs", "rowData", "defaultColDef", "searchPlaceholder", "actions"])
              ]))
    ]),
    (showIssueModal.value)
      ? (_openBlock(), _createBlock(IssueFormModal, {
          key: 0,
          visible: showIssueModal.value,
          issue: selectedIssue.value,
          "project-id": _ctx.projectId,
          intervenants: intervenants.value,
          onClose: closeIssueModal,
          onIssueSaved: onIssueSaved
        }, null, 8, ["visible", "issue", "project-id", "intervenants"]))
      : _createCommentVNode("", true),
    (showDeleteConfirm.value)
      ? (_openBlock(), _createBlock(ConfirmModal, {
          key: 1,
          isVisible: showDeleteConfirm.value,
          title: _ctx.$t('issues.deleteIssue'),
          details: deleteMessage.value,
          "confirm-text": _ctx.$t('common.delete'),
          "cancel-text": _ctx.$t('common.cancel'),
          type: "danger",
          onConfirm: confirmDelete,
          onCancel: cancelDelete
        }, null, 8, ["isVisible", "title", "details", "confirm-text", "cancel-text"]))
      : _createCommentVNode("", true)
  ]))
}
}

})